/**
 * Secure Local Storage - Machine-bound encrypted storage for QuickClip credentials
 *
 * Security model:
 * - Data is encrypted with a key derived from UUID + machine-specific salt + random keySalt
 * - Storage key is derived from UUID + machine salt (obfuscated)
 * - UUID is NOT stored in the data - attacker must know UUID to decrypt
 * - Uses PBKDF2 with 100k iterations for slow key derivation
 * - Data expires after 7 days to limit exposure
 */

import { getMachineSalt } from './machineSalt';

const STORAGE_VERSION = '2'; // Bumped version for new format
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const PBKDF2_ITERATIONS = 100000;

interface SecureStorageData {
  v: string;        // version
  pin: string;      // encrypted PIN (base64)
  salt: string;     // server salt
  keySalt: string;  // random salt for key derivation (base64)
  ts: number;       // timestamp
  isCreator?: boolean; // whether user is creator
  // NOTE: uuid is NOT stored - it's derived from the storage key lookup
}

/**
 * Generate random bytes as base64
 */
function generateRandomSalt(length: number = 16): string {
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return btoa(String.fromCharCode(...bytes));
}

/**
 * Derive the storage key (localStorage key name)
 * This is obfuscated and machine-specific
 * Used to locate the stored data for a given UUID
 */
async function deriveStorageKey(uuid: string): Promise<string> {
  const machineSalt = getMachineSalt();
  const encoder = new TextEncoder();
  const input = `${uuid}:QC_STORAGE:${machineSalt}`;
  const data = encoder.encode(input);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hash))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
    .substring(0, 16);
}

/**
 * Derive the encryption key using PBKDF2
 * Slow derivation to resist brute force attacks
 */
async function deriveEncryptionKey(uuid: string, keySalt: string): Promise<CryptoKey> {
  const machineSalt = getMachineSalt();
  const encoder = new TextEncoder();

  // Combine uuid + machineSalt as password
  const password = `${uuid}:QC_ENCRYPT:${machineSalt}`;
  const passwordData = encoder.encode(password);

  // Import password as key material
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    passwordData,
    'PBKDF2',
    false,
    ['deriveKey']
  );

  // Decode keySalt from base64
  const saltBytes = Uint8Array.from(atob(keySalt), c => c.charCodeAt(0));

  // Derive AES-256 key using PBKDF2
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBytes,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt plaintext using AES-GCM
 */
async function encryptWithKey(plaintext: string, key: CryptoKey): Promise<string> {
  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(plaintext)
  );

  // Combine IV + ciphertext
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(ciphertext), iv.length);

  // Convert to base64
  return btoa(String.fromCharCode(...combined));
}

/**
 * Decrypt ciphertext using AES-GCM
 */
async function decryptWithKey(ciphertext: string, key: CryptoKey): Promise<string> {
  const decoder = new TextDecoder();

  // Decode base64
  const combined = new Uint8Array(
    atob(ciphertext)
      .split('')
      .map(c => c.charCodeAt(0))
  );

  const iv = combined.slice(0, 12);
  const encrypted = combined.slice(12);

  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    encrypted
  );

  return decoder.decode(decrypted);
}

/**
 * Save credentials to secure local storage
 *
 * @param uuid - Clip UUID
 * @param pin - Plaintext PIN (will be encrypted)
 * @param salt - Server salt
 * @param isCreator - Whether user is the creator
 */
export async function saveCredentials(
  uuid: string,
  pin: string,
  salt: string,
  isCreator = false
): Promise<void> {
  try {
    // Generate random salt for key derivation
    const keySalt = generateRandomSalt(16);

    // Derive encryption key
    const encKey = await deriveEncryptionKey(uuid, keySalt);

    // Encrypt PIN
    const encryptedPin = await encryptWithKey(pin, encKey);

    // Store data (WITHOUT uuid)
    const data: SecureStorageData = {
      v: STORAGE_VERSION,
      pin: encryptedPin,
      salt,
      keySalt,
      ts: Date.now(),
      isCreator,
    };

    const storageKey = await deriveStorageKey(uuid);
    localStorage.setItem(storageKey, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save credentials:', error);
    throw new Error('Failed to save credentials securely');
  }
}

/**
 * Load credentials from secure local storage
 *
 * @param uuid - Clip UUID
 * @returns Object with pin, salt, and isCreator, or null if not found/expired/invalid
 */
export async function loadCredentials(uuid: string): Promise<{
  pin: string;
  salt: string;
  isCreator: boolean;
} | null> {
  try {
    const storageKey = await deriveStorageKey(uuid);
    const raw = localStorage.getItem(storageKey);

    if (!raw) {
      return null;
    }

    const data: SecureStorageData = JSON.parse(raw);

    // Version check
    if (data.v !== STORAGE_VERSION) {
      localStorage.removeItem(storageKey);
      return null;
    }

    // Expiration check
    if (Date.now() - data.ts > MAX_AGE_MS) {
      localStorage.removeItem(storageKey);
      return null;
    }

    // Derive encryption key using stored keySalt
    const encKey = await deriveEncryptionKey(uuid, data.keySalt);

    // Decrypt PIN
    const pin = await decryptWithKey(data.pin, encKey);

    return { pin, salt: data.salt, isCreator: data.isCreator ?? false };
  } catch (error) {
    // Decryption failed (wrong machine, corrupted data, etc.)
    // Silently clear the invalid storage
    await clearCredentials(uuid);
    return null;
  }
}

/**
 * Clear credentials for a specific clip
 *
 * @param uuid - Clip UUID
 */
export async function clearCredentials(uuid: string): Promise<void> {
  try {
    const storageKey = await deriveStorageKey(uuid);
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.error('Failed to clear credentials:', error);
  }
}

/**
 * Clear all QuickClip credentials from localStorage
 * Useful for logout or security cleanup
 */
export async function clearAllCredentials(): Promise<void> {
  const keys = Object.keys(localStorage);

  // Remove all items that look like our storage keys (16 char hex)
  for (const key of keys) {
    if (/^[a-f0-9]{16}$/i.test(key)) {
      localStorage.removeItem(key);
    }
  }
}

/**
 * Check if credentials exist for a clip
 *
 * @param uuid - Clip UUID
 * @returns true if credentials exist and are not expired
 */
export async function hasCredentials(uuid: string): Promise<boolean> {
  try {
    const storageKey = await deriveStorageKey(uuid);
    const raw = localStorage.getItem(storageKey);

    if (!raw) return false;

    const data: SecureStorageData = JSON.parse(raw);

    if (data.v !== STORAGE_VERSION) return false;
    if (Date.now() - data.ts > MAX_AGE_MS) return false;

    return true;
  } catch {
    return false;
  }
}

/**
 * Get storage info for debugging (sanitized)
 */
export async function getStorageInfo(): Promise<{
  count: number;
  machineSaltAvailable: boolean;
}> {
  const keys = Object.keys(localStorage);
  const ourKeys = keys.filter(key => /^[a-f0-9]{16}$/i.test(key));

  return {
    count: ourKeys.length,
    machineSaltAvailable: true,
  };
}
